import React from 'react';

import { MediaAsset, MediaAssetType } from 'types';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import MediaAssetPicker from '../../../components/admin/MediaAssetPicker';
import type { AdminFlow, FlowMetadata } from './types';
import { getFlowThemeInheritance, isValidEstimatedDuration } from './utils';
import { FlowThemePicker } from './FlowThemePicker';

export type FlowMetadataFormProps = {
  metadata: FlowMetadata;
  onFieldChange: (field: keyof AdminFlow, value: string | boolean | number | null) => void;
  onMediaChange: (asset: MediaAsset | null) => void;
  frequencyOptions: readonly { value: string; label: string }[];
  isSaving: boolean;
  hasRoleplayQuestion?: boolean;
  mediaTypes?: MediaAssetType[];
};

export type FlowBasicsFieldsProps = Pick<
  FlowMetadataFormProps,
  'metadata' | 'onFieldChange' | 'frequencyOptions' | 'isSaving'
>;

/** Name, learner-facing title, duration, frequency, public access, and description. */
export const FlowBasicsFields: React.FC<FlowBasicsFieldsProps> = ({
  metadata,
  onFieldChange,
  frequencyOptions,
  isSaving,
}) => {
  const hasInvalidDuration = !isValidEstimatedDuration(metadata.estimated_duration_minutes);
  return (
    <>
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="flow-name">Name</Label>
          <Input
            id="flow-name"
            value={metadata.name ?? ''}
            onChange={(event) => onFieldChange('name', event.target.value)}
            disabled={isSaving}
            aria-describedby="flow-name-hint"
          />
          <p id="flow-name-hint" className="text-xs text-muted-foreground">
            Internal identifier used in module links. Use the learner-facing title to change what learners see.
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flow-title">Learner-facing title</Label>
          <Input
            id="flow-title"
            maxLength={100}
            value={metadata.title ?? ''}
            onChange={(event) => onFieldChange('title', event.target.value)}
            aria-describedby="flow-title-hint"
            disabled={isSaving}
          />
          <p id="flow-title-hint" className="text-xs text-muted-foreground">
            Optional, up to 100 characters. Leave blank to use the internal name.
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flow-duration">Estimated duration (minutes)</Label>
          <Input
            id="flow-duration"
            type="text"
            inputMode="numeric"
            value={metadata.estimated_duration_minutes ?? ''}
            onChange={(event) => onFieldChange(
              'estimated_duration_minutes', event.target.value === '' ? null : event.target.value,
            )}
            aria-invalid={hasInvalidDuration || undefined}
            aria-describedby={hasInvalidDuration ? 'flow-duration-error' : 'flow-duration-hint'}
            disabled={isSaving}
          />
          {hasInvalidDuration ? (
            <p id="flow-duration-error" role="alert" className="text-xs text-destructive">
              Enter a whole number of minutes greater than zero, or leave blank.
            </p>
          ) : (
            <p id="flow-duration-hint" className="text-xs text-muted-foreground">
              Optional. Leave blank if no estimate is available.
            </p>
          )}
        </div>
        {
          <>
            <div className="space-y-1">
              <Label htmlFor="flow-frequency">Frequency</Label>
              <Select
                value={metadata.frequency ?? ''}
                onValueChange={(value) => onFieldChange('frequency', value)}
                disabled={isSaving}
              >
                <SelectTrigger id="flow-frequency">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  {frequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="flow-is-public">Public Module</Label>
              <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3">
                <p className="text-xs text-muted-foreground">
                  Accessible to all authenticated users without assignment.
                </p>
                <Checkbox
                  id="flow-is-public"
                  checked={Boolean(metadata.is_public)}
                  onCheckedChange={(checked) => onFieldChange('is_public', checked === true)}
                  disabled={isSaving}
                />
              </div>
            </div>
          </>
        }
      </div>

      <div className="space-y-1 w-full">
        <Label htmlFor="flow-description">Description</Label>
        <Textarea
          id="flow-description"
          rows={3}
          value={metadata.description ?? ''}
          onChange={(event) => onFieldChange('description', event.target.value)}
          disabled={isSaving}
        />
      </div>
    </>
  );
};

export type FlowQuizVoiceFieldsProps = Pick<
  FlowMetadataFormProps,
  'metadata' | 'onFieldChange' | 'isSaving' | 'hasRoleplayQuestion'
>;

/** Quiz Mode with its settings, and Voice Mode. */
export const FlowQuizVoiceFields: React.FC<FlowQuizVoiceFieldsProps> = ({
  metadata,
  onFieldChange,
  isSaving,
  hasRoleplayQuestion = false,
}) => {
  const quizMode = metadata.quiz_mode === 'pass_only' ? 'pass_only' : 'graded';
  return (
    <>
      <div className="space-y-3 rounded-md border border-border/40 p-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="flow-is-quiz">Quiz Mode</Label>
            <p className="text-xs text-muted-foreground">
              {hasRoleplayQuestion
                ? 'Roleplay questions use the dedicated practice runtime and cannot be quizzes.'
                : 'Toggle whether this module behaves as a graded quiz or pass-only survey.'}
            </p>
          </div>
          <Switch
            id="flow-is-quiz"
            checked={!hasRoleplayQuestion && Boolean(metadata.is_quiz)}
            onCheckedChange={(checked) => onFieldChange('is_quiz', checked)}
            disabled={isSaving || hasRoleplayQuestion}
          />
        </div>

        {metadata.is_quiz && !hasRoleplayQuestion ? (
          <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="flow-quiz-mode">Assessment mode</Label>
              <Select
                value={quizMode}
                onValueChange={(value) =>
                  onFieldChange('quiz_mode', value === 'pass_only' ? 'pass_only' : 'graded')
                }
                disabled={isSaving}
              >
                <SelectTrigger id="flow-quiz-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="graded">Graded Quiz</SelectItem>
                  <SelectItem value="pass_only">Pass-Only Survey</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="flow-passing-score">Passing score (%)</Label>
              <Input
                id="flow-passing-score"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={metadata.passing_score_percentage ?? ''}
                onChange={(event) =>
                  onFieldChange(
                    'passing_score_percentage',
                    event.target.value === '' ? null : Number(event.target.value),
                  )
                }
                disabled={isSaving || quizMode === 'pass_only'}
              />
              {quizMode === 'pass_only' ? (
                <p className="text-xs text-muted-foreground">Pass-only surveys do not use a passing score.</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="flow-max-attempts">Max attempts</Label>
              <Input
                id="flow-max-attempts"
                type="number"
                min={1}
                step="1"
                value={metadata.max_attempts ?? ''}
                onChange={(event) =>
                  onFieldChange(
                    'max_attempts',
                    event.target.value === '' ? null : Number(event.target.value),
                  )
                }
                disabled={isSaving || quizMode === 'pass_only'}
              />
              <p className="text-xs text-muted-foreground">
                {quizMode === 'pass_only'
                  ? 'Pass-only surveys are single attempt.'
                  : 'Leave blank for unlimited attempts.'}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="flow-randomize-questions">Randomize question order</Label>
              <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3">
                <p className="text-xs text-muted-foreground">Shuffle question order per attempt.</p>
                <Switch
                  id="flow-randomize-questions"
                  checked={Boolean(metadata.randomize_question_order)}
                  onCheckedChange={(checked) => onFieldChange('randomize_question_order', checked)}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="flow-randomize-options">Randomize option order</Label>
              <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3">
                <p className="text-xs text-muted-foreground">Shuffle option order per attempt.</p>
                <Switch
                  id="flow-randomize-options"
                  checked={Boolean(metadata.randomize_option_order)}
                  onCheckedChange={(checked) => onFieldChange('randomize_option_order', checked)}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="flow-allow-retake">Allow retake after passing</Label>
              <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3">
                <p className="text-xs text-muted-foreground">
                  If enabled, users can start another attempt after a pass.
                </p>
                <Switch
                  id="flow-allow-retake"
                  checked={Boolean(metadata.allow_retake_after_pass)}
                  onCheckedChange={(checked) => onFieldChange('allow_retake_after_pass', checked)}
                  disabled={isSaving || quizMode === 'pass_only'}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-3 rounded-md border border-border/40 p-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="flow-voice-mode">Voice Mode</Label>
            <p className="text-xs text-muted-foreground">
              Allow eligible learners to complete this module through spoken questions and activities, including supported quizzes and roleplay.
            </p>
          </div>
          <Switch
            id="flow-voice-mode"
            checked={Boolean(metadata.voice_mode_enabled)}
            onCheckedChange={(checked) => onFieldChange('voice_mode_enabled', checked)}
            disabled={isSaving}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Voice Mode is opt-in for each module. Initial microphone permission is required; voice-compatible runtimes handle the spoken experience.
        </p>
      </div>
    </>
  );
};

export type FlowThemeImageFieldsProps = Pick<
  FlowMetadataFormProps,
  'metadata' | 'onFieldChange' | 'onMediaChange' | 'isSaving' | 'mediaTypes'
>;

/** The animated theme and the module image. */
export const FlowThemeImageFields: React.FC<FlowThemeImageFieldsProps> = ({
  metadata,
  onFieldChange,
  onMediaChange,
  isSaving,
  mediaTypes = ['image'],
}) => {
  const {
    inherited_theme: inheritedTheme,
    inherited_theme_source: inheritedThemeSource,
    inherited_theme_course: inheritedThemeCourse,
    theme_courses: themeCourses,
  } = metadata;
  const inheritance = React.useMemo(
    () => getFlowThemeInheritance({
      inherited_theme: inheritedTheme,
      inherited_theme_source: inheritedThemeSource,
      inherited_theme_course: inheritedThemeCourse,
      theme_courses: themeCourses,
    }),
    [inheritedTheme, inheritedThemeCourse, inheritedThemeSource, themeCourses],
  );

  return (
    <>
      <FlowThemePicker
        value={metadata.theme}
        onChange={(theme) => onFieldChange('theme', theme)}
        disabled={isSaving}
        inheritance={inheritance}
        lifePlanAllowed={Boolean(metadata.life_plan_theme_allowed)}
      />

      <MediaAssetPicker
        value={metadata.media ?? null}
        onChange={onMediaChange}
        mediaTypes={mediaTypes}
        label="Module image"
        description="Select an image asset to represent this module."
        disabled={isSaving}
      />
    </>
  );
};

/** Every module setting in editor order: basics, quiz and voice, then theme and image. */
export const FlowMetadataForm: React.FC<FlowMetadataFormProps> = ({
  metadata,
  onFieldChange,
  onMediaChange,
  frequencyOptions,
  isSaving,
  hasRoleplayQuestion = false,
  mediaTypes = ['image'],
}) => (
  <>
    <FlowBasicsFields
      metadata={metadata}
      onFieldChange={onFieldChange}
      frequencyOptions={frequencyOptions}
      isSaving={isSaving}
    />
    <FlowQuizVoiceFields
      metadata={metadata}
      onFieldChange={onFieldChange}
      isSaving={isSaving}
      hasRoleplayQuestion={hasRoleplayQuestion}
    />
    <FlowThemeImageFields
      metadata={metadata}
      onFieldChange={onFieldChange}
      onMediaChange={onMediaChange}
      isSaving={isSaving}
      mediaTypes={mediaTypes}
    />
  </>
);
